/**
 * Bulk runs (ADR-0107): one agent over many rows — a CSV file, a data table or rows a program
 * sends — with progress, per-row results, retry of failed rows, a cost cap and an export.
 */
import type { Http } from "./http";

const enc = encodeURIComponent;

export type BatchStatus = "running" | "finished" | "stopped" | "cancelled";
export type BatchRowStatus = "waiting" | "running" | "done" | "failed" | "not_run";

export interface Batch {
  batch_id: string;
  agent: string;
  agent_ref: string;
  title: string;
  /** csv | rows | the data table's name */
  source: string;
  user_id: string | null;
  status: BatchStatus;
  stop_reason: string | null;
  total: number;
  concurrency: number;
  max_cost_usd: number;
  cost_usd: number;
  columns: string[];
  created_at: string | null;
  finished_at: string | null;
  counts: Record<BatchRowStatus, number>;
}
export interface BatchRowResult {
  index: number;
  input: Record<string, unknown>;
  status: BatchRowStatus;
  run_id: string | null;
  attempts: number;
  answer: string | null;
  output: Record<string, unknown> | null;
  error: string | null;
  cost_usd: number;
  updated_at: string | null;
}
export interface BatchIn {
  agent: string;
  title?: string;
  source: "csv" | "table" | "rows";
  csv?: string;
  table?: string;
  rows?: Record<string, unknown>[];
  question?: string;
  concurrency: number;
  max_cost_usd: number;
  priority?: "high" | "normal" | "low";
}

/** How far a batch is (0–100), counting rows that have ended. */
export function batchProgress(b: Pick<Batch, "total" | "counts">): number {
  if (!b.total) return 100;
  const ended = b.counts.done + b.counts.failed + b.counts.not_run;
  return Math.round((ended / b.total) * 100);
}

/** The most a batch may cost: the cap the person sets, suggested from the agent's per-run budget. */
export function suggestedCap(rows: number, perRun: number): number {
  return Math.max(0.5, Math.round(rows * perRun * 100) / 100);
}

export function bulkRunsApi(http: Http) {
  return {
    list: (agent?: string) => http.get<Batch[]>("orchestrator", "/v1/batches", { agent }),
    get: (id: string) => http.get<Batch>("orchestrator", `/v1/batches/${enc(id)}`),
    rows: (id: string, q: { status?: BatchRowStatus; limit?: number; offset?: number } = {}) => http.get<{ rows: BatchRowResult[]; total: number }>("orchestrator", `/v1/batches/${enc(id)}/rows`, q),
    start: (body: BatchIn) => http.post<Batch>("orchestrator", "/v1/batches", body),
    cancel: (id: string) => http.post<{ cancelling: boolean }>("orchestrator", `/v1/batches/${enc(id)}/cancel`, {}),
    retry: (id: string, includeNotRun = true) => http.post<{ rows: number; attempt: number }>("orchestrator", `/v1/batches/${enc(id)}/retry`, { include_not_run: includeNotRun }),
    exportCsv: async (id: string): Promise<string> => (await http.raw("orchestrator", `/v1/batches/${enc(id)}/export`)).text(),
  };
}
